import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { RecentSearches } from '@theme/recent-searches';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLFormElement} [form] - The search form.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.addEventListener('click', this.#handleResultClick, { signal });
    this.refs.form?.addEventListener('submit', () => RecentSearches.addTerm(this.refs.searchInput.value), {
      signal,
    });
    this.#renderRecentSearches();

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Remembers the search term when the shopper follows a result or a search shortcut.
   * @param {MouseEvent} event - The mouse event
   */
  #handleResultClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a');
    if (!link || !this.refs.predictiveSearchResults.contains(link)) return;

    RecentSearches.addTerm(link.dataset.searchTerm ?? this.refs.searchInput.value);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * The search matching options (fields, sold-out handling, prefix) rendered as hidden inputs
   * by the search-options snippet.
   * @returns {HTMLInputElement[]}
   */
  get #searchOptionInputs() {
    return Array.from(this.querySelectorAll('input[type="hidden"][name^="options["]'));
  }

  /**
   * Builds a search results page URL that keeps the theme's search matching options.
   * @param {string} term - The search term
   * @returns {URL}
   */
  #buildSearchPageUrl(term) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', term);
    for (const input of this.#searchOptionInputs) {
      url.searchParams.set(input.name, input.value);
    }
    return url;
  }

  /**
   * Fills the recent searches slot rendered by the search-suggestions snippet.
   */
  #renderRecentSearches() {
    const wrapper = this.querySelector('[data-recent-searches]');
    const list = wrapper?.querySelector('[data-recent-searches-list]');
    if (!(wrapper instanceof HTMLElement) || !list) return;

    const terms = RecentSearches.getTerms();
    wrapper.hidden = terms.length === 0;

    list.replaceChildren(
      ...terms.map((term) => {
        const item = document.createElement('li');
        item.className = 'predictive-search-results__card--query';
        item.setAttribute('ref', 'resultsItems[]');

        const link = document.createElement('a');
        link.className = 'pills__pill predictive-search-results__pill';
        link.href = this.#buildSearchPageUrl(term).toString();
        link.dataset.searchTerm = term;

        const label = document.createElement('span');
        label.textContent = term;

        link.append(label);
        item.append(link);
        return item;
      })
    );
  }

  /**
   * Clears the recent searches.
   * @param {Event} event - The event.
   */
  clearRecentSearches(event) {
    event.stopPropagation();

    RecentSearches.clearTerms();
    this.#renderRecentSearches();
    this.refs.searchInput.focus();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          RecentSearches.addTerm(this.refs.searchInput.value);
          window.location.href = this.#buildSearchPageUrl(this.refs.searchInput.value).toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    for (const input of this.#searchOptionInputs) {
      url.searchParams.set(input.name.replace(/^options/, 'resources[options]'), input.value);
    }

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    Promise.all([
      sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url),
      this.#getBrandBundlesHTML(searchTerm),
    ])
      .then(([searchMarkup, bundlesMarkup]) => {
        const resultsMarkup = this.#addBrandBundles(searchMarkup, bundlesMarkup);
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries), but not the
        // popular searches and picks shown as shortcuts when nothing matched
        const resultCount = predictiveSearchResults.querySelectorAll(
          '[ref="resultsItems[]"]:not([data-search-suggestion] *)'
        ).length;
        deferredPromise.resolve({ totalCount: resultCount });

        if (resultCount === 0) this.#loadPersonalRecommendations(abortController.signal);
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Renders the in-stock bundles of the brand the search names, newest first
   * (search-brand-bundles section). Uses the search page route, which considers up to 50
   * bundles, so the newest are found even when other brands' bundles match too. Resolves to
   * an empty string when there are none or the request fails, so the search results still show.
   * @param {string} searchTerm - The term to search for
   * @returns {Promise<string>}
   */
  async #getBrandBundlesHTML(searchTerm) {
    if (searchTerm.trim().length < 3) return '';

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', `${searchTerm.trim()} AND tag:actbund`);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('options[unavailable_products]', 'hide');

    try {
      return await sectionRenderer.getSectionHTML('search-brand-bundles', true, url);
    } catch {
      return '';
    }
  }

  /**
   * Puts the brand's 4 newest bundles above the matching products, and drops them from the
   * lists below.
   * @param {string} searchMarkup - The predictive search section markup
   * @param {string} bundlesMarkup - The search-brand-bundles section markup
   * @returns {string}
   */
  #addBrandBundles(searchMarkup, bundlesMarkup) {
    if (!searchMarkup || !bundlesMarkup) return searchMarkup;

    const parser = new DOMParser();
    const bundles = parser.parseFromString(bundlesMarkup, 'text/html').querySelector('[data-brand-bundles]');
    const bundleItems = Array.from(bundles?.querySelectorAll('[data-brand-bundle]') ?? []).slice(0, 4);
    if (!bundles || !bundleItems.length) return searchMarkup;

    const results = parser.parseFromString(searchMarkup, 'text/html');
    const products = results.getElementById('predictive-search-products');
    if (!products) return searchMarkup;

    const bundleIds = new Set(bundleItems.map((item) => item.getAttribute('data-product-id')));
    for (const card of results.querySelectorAll('.predictive-search-results__card--product')) {
      const id = card.querySelector('[data-product-id]')?.getAttribute('data-product-id');
      if (id && bundleIds.has(id)) card.remove();
    }

    // Same markup as the predictive-search-products-list snippet
    const list = results.createElement('div');
    list.id = 'predictive-search-bundles';
    list.className = 'predictive-search-results__products';
    const title = results.createElement('h4');
    title.className = 'predictive-search-results__title';
    title.textContent = bundles.querySelector('.main-collection-grid__title')?.textContent.trim() ?? '';
    const cards = results.createElement('ul');
    cards.className = 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled';
    for (const item of bundleItems) {
      const card = results.createElement('li');
      card.className = 'predictive-search-results__card predictive-search-results__card--product';
      card.setAttribute('ref', 'resultsItems[]');
      card.append(...item.childNodes);
      cards.append(card);
    }
    list.append(title, cards);

    products.before(list);
    if (!products.querySelector('.predictive-search-results__card--product')) products.remove();

    // Several results now, so Enter opens the search page instead of the only product
    results.querySelector('[data-single-result-url]')?.remove();

    return results.body.innerHTML;
  }

  /**
   * When a search finds nothing, swap the fallback picks for recommendations based on the
   * product the shopper viewed most recently.
   * @param {AbortSignal} signal - Aborted when a newer search starts
   */
  async #loadPersonalRecommendations(signal) {
    const [productId] = RecentlyViewed.getProducts();
    const container = this.refs.predictiveSearchResults.querySelector('[data-search-recommendations]');
    if (!productId || !container || !this.dataset.sectionId || !Theme.routes.product_recommendations_url) return;

    const url = new URL(Theme.routes.product_recommendations_url, location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '4');
    url.searchParams.set('intent', 'related');

    try {
      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, true, url);
      if (signal.aborted || !container.isConnected) return;

      const products = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('#predictive-search-products');
      if (!products?.querySelector('.predictive-search-results__card')) return;

      morph(container, `<div>${products.outerHTML}</div>`);
    } catch {
      // Keep the fallback picks
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#renderRecentSearches();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
